import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { num } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, ChevronDown, ExternalLink } from "lucide-react";
import { CURRENT_SEASON } from "@/lib/season";

export const ProductIssues = () => {
  const [open, setOpen] = useState(false);
  const [season, setSeason] = useState(CURRENT_SEASON);
  const { data: all = [] } = useQuery({ queryKey: ["product-issues"], queryFn: async () => (await api.get("/products/issues")).data });
  if (!all.length) return null;
  const seasons = [...new Set(all.map((p) => p.season).filter(Boolean))].sort();
  const count = (s) => all.filter((p) => s === "tutte" || p.season === s).length;
  const data = all.filter((p) => season === "tutte" || p.season === season);
  return (
    <Card className="mb-6 border-amber-500/30 bg-amber-500/5 p-4" data-testid="product-issues">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center justify-between text-left" data-testid="product-issues-toggle">
        <span className="flex items-center gap-2 text-sm font-medium">
          <AlertTriangle className="h-4 w-4 text-amber-400" />
          {num(all.length)} prodotti da sistemare su Shopify · senza categoria o con giacenza negativa
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="product-issues-seasons">
          {["tutte", ...seasons].map((s) => (
            <button key={s} onClick={() => setSeason(s)} data-testid={`product-issues-season-${s}`}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${season === s ? "border-primary bg-primary text-primary-foreground" : "border-border/60 hover:bg-secondary/50"}`}>
              {s === "tutte" ? "Tutte" : s} ({num(count(s))})
            </button>
          ))}
        </div>
      )}
      {open && (
        <div className="mt-3 divide-y divide-border/50">
          {!data.length && <p className="py-2 text-sm text-muted-foreground">Nessun prodotto da sistemare in questa stagione.</p>}
          {data.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm" data-testid={`product-issue-${p.sku}`}>
              <div className="min-w-0">
                <p className="font-medium">{p.name} <span className="font-mono-erp text-[11px] text-muted-foreground">{p.sku} · {p.season}</span></p>
                <p className="text-xs text-muted-foreground">
                  Giacenza totale {num(p.stock)} pz
                  {p.negative_sizes.length > 0 && <> · negative: {p.negative_sizes.map((s) => `${s.size} (${s.quantity})`).join(", ")}</>}
                  {p.is_gift && <> · è una gift card, non un capo</>}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {p.reasons.map((r) => <Badge key={r} variant="outline" className="border-amber-500/50 text-[10px] text-amber-400">{r}</Badge>)}
                {p.shopify_url && (
                  <a href={p.shopify_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-primary hover:underline" data-testid={`product-issue-link-${p.sku}`}>
                    Apri su Shopify<ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
